import type { i18n as I18nInstance } from 'i18next';

export type CatalogSection =
  | 'skills'
  | 'seniority'
  | 'specializations'
  | 'industries'
  | 'languages'
  | 'regions'
  | 'cities'
  | 'education'
  | 'certificates'
  | 'tools'
  | 'fees'
  | 'stages'
  | 'other';

type CatalogBundle = Partial<Record<CatalogSection, Record<string, string>>>;

type TranslationBundle = {
  catalog?: CatalogBundle;
};

function getCatalogFromBundle(
  bundle: TranslationBundle | undefined,
): CatalogBundle | undefined {
  return bundle?.catalog;
}

function lookupInCatalog(
  catalog: CatalogBundle | undefined,
  section: CatalogSection,
  name: string,
): string | undefined {
  if (!catalog || !name) {
    return undefined;
  }

  const sectionMap = catalog[section];
  if (sectionMap && name in sectionMap) {
    return sectionMap[name];
  }

  const otherMap = catalog.other;
  if (otherMap && name in otherMap) {
    return otherMap[name];
  }

  return undefined;
}

/** Dot-safe lookup for API-driven entity labels under `catalog.*`. */
export function resolveCatalogSection(
  i18nInstance: I18nInstance,
  section: CatalogSection,
  name: string,
): string {
  if (!name) {
    return name;
  }

  const fallback = i18nInstance.options.fallbackLng;
  const fallbacks: string[] = Array.isArray(fallback)
    ? fallback.filter((lng): lng is string => typeof lng === 'string')
    : typeof fallback === 'string'
      ? [fallback]
      : [];
  const languages = [i18nInstance.language, ...fallbacks];

  for (const lng of languages) {
    if (typeof lng !== 'string') {
      continue;
    }

    const bundle = i18nInstance.getResourceBundle(lng, 'translation') as
      TranslationBundle | undefined;
    const found = lookupInCatalog(getCatalogFromBundle(bundle), section, name);
    if (found !== undefined) {
      return found;
    }
  }

  return name;
}

/** Dot-safe lookup in `catalog.other` with language fallback. */
export function resolveCatalogOther(
  i18nInstance: I18nInstance,
  name: string,
): string {
  return resolveCatalogSection(i18nInstance, 'other', name);
}
